import { useLazyQuery, useMutation } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { UpdateProjectContent } from '@deltek/specification-client/core/Models/projectProductProperty'
import { InsertContentItemsFromHtml } from 'api/graphQL/mutations/contentInsert.mutations'
import {
    GetContentInsertStatusQuery,
    GetProductTypeContentInsertStatusQuery,
} from 'api/graphQL/queries/contentInsert.queries'
import {
    ContentInsertVariables,
    GetContentInsertStatusResponse,
    InsertContentItemsFromHtmlInput,
    InsertContentItemsFromHtmlResponse,
} from 'api/graphQL/types/contentInsert.types'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import ProgressDialog from 'components/ToMoveToSCS/ProgressDialog'
import React from 'react'

interface HtmlParagraphPasteProgressDialogProps {
    /** Node currently selected in Project Elements tree. */
    selectedProjectElementNode?: Partial<ProjectElementNode> | null

    /** Variables for the content-insert mutation. */
    contentInsertVariables: Partial<ContentInsertVariables>

    /** Called when the paste process is completed (with or without errors).
     * @param result - The resulting content if there were no errors. Null if there was an error.
     */
    onPasteCompleted: (result: Partial<UpdateProjectContent>) => void

    /** Called when user chooses 'Cancel' in a warning confirmation dialog
     * before any paragraphs can be pasted.
     */
    onPasteCancelled: () => void

    /** Called if the API reports an error during the paste process. */
    onPasteError: (errorMessage: string | null | undefined) => void
}

const StatusText_Validating: string = 'Validating content, just a moment...'
const StatusText_Importing: string =
    "Specpoint is importing your content into the database. This shouldn't take long..."

/** Dialog that shows the progress of an HTML paragraph paste
 * and calls a callback when the paste is completed.
 */
const HtmlParagraphPasteProgressDialog: React.FC<HtmlParagraphPasteProgressDialogProps> = ({
    selectedProjectElementNode,
    contentInsertVariables,
    onPasteCompleted,
    onPasteCancelled,
    onPasteError,
}: HtmlParagraphPasteProgressDialogProps) => {
    const [instanceId, setInstanceId] = React.useState<string>()
    const [progress, setProgress] = React.useState<number>(0)
    const [statusText, setStatusText] = React.useState<string>(
        StatusText_Validating
    )
    const [isPasteInProgress, setIsPasteInProgress] = React.useState<boolean>(
        false
    )
    const [warningText, setWarningText] = React.useState<string>()
    const [isWarningIgnored, setIsWarningIgnored] = React.useState<boolean>(
        false
    )

    const onStatusQueryCompleted = React.useCallback(
        (data: GetContentInsertStatusResponse) => {
            const contentInsertState = data?.getContentInsertStatus?.state?.toUpperCase()
            switch (contentInsertState) {
                case 'INPROGRESS':
                    const progressPercentage =
                        data?.getContentInsertStatus?.progressPercentage
                    if (
                        progressPercentage !== undefined &&
                        progressPercentage !== progress
                    ) {
                        setProgress(progressPercentage)
                    }
                    // still in progress; poll again after a short time
                    setTimeout(() => queryForStatus(), 1000)
                    break
                case 'COMPLETE':
                    setProgress(100)
                    setIsPasteInProgress(false)
                    const result =
                        data?.getContentInsertStatus?.updatedProjectContent
                    if (result) {
                        // To make sure user sees full progress bar, wait a split second before calling
                        // the completion handler.
                        // This was the only way I could find to ensure the "full progress bar" animation
                        // shows before the dialog is closed. If we call the callback directly,
                        // it ends up closing the dialog before the progress bar updates,
                        // whether we use a selective render (e.g., isPasteInProgress && <HtmlParagraphPasteDialog>)
                        // or set the "open" prop on the progress dialog.
                        setTimeout(() => onPasteCompleted(result), 500)
                    }
                    break
                case 'ERROR':
                    setIsPasteInProgress(false)
                    handlePasteError(data?.getContentInsertStatus?.errorMessage)
                    break
            }
        },
        [
            contentInsertVariables,
            selectedProjectElementNode,
            progress,
            onPasteCompleted,
            onPasteError,
        ]
    )
    const queryForStatus = React.useCallback(() => {
        if (
            selectedProjectElementNode?.elementType ===
            ProjectElementType.PRODUCTTYPE
        ) {
            getProductTypeContentInsertStatus()
        } else {
            getFamilyContentInsertStatus()
        }
    }, [instanceId, selectedProjectElementNode])

    const [getFamilyContentInsertStatus] = useLazyQuery<
        GetContentInsertStatusResponse
    >(GetContentInsertStatusQuery(6), {
        variables: {
            instanceId: instanceId,
            partType: contentInsertVariables.partType,
            rootArticleContentItemIds:
                contentInsertVariables.rootArticleContentItemIds,
            productFamilyId: contentInsertVariables.productFamilyId,
        },
        fetchPolicy: 'no-cache',
        onCompleted: (data) => onStatusQueryCompleted(data),
        onError: (error) => {
            handlePasteError(error.message)
        },
    })

    const [getProductTypeContentInsertStatus] = useLazyQuery<
        GetContentInsertStatusResponse
    >(GetProductTypeContentInsertStatusQuery(6), {
        variables: {
            instanceId: instanceId,
            partType: contentInsertVariables.partType,
            rootArticleContentItemIds:
                contentInsertVariables.rootArticleContentItemIds,
            productFamilyId: contentInsertVariables.productFamilyId,
            nodeId: selectedProjectElementNode?.id,
        },
        fetchPolicy: 'no-cache',
        onCompleted: (data) => onStatusQueryCompleted(data),
        onError: (error) => {
            handlePasteError(error.message)
        },
    })

    const [pasteContentItemsFromHtml] = useMutation<
        InsertContentItemsFromHtmlResponse,
        {
            input: Partial<InsertContentItemsFromHtmlInput>
            partType: string
            productFamilyId: string
            rootArticleContentItemIds: string[]
        }
    >(InsertContentItemsFromHtml, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            if (data.insertContentItemsFromHtml?.warnings?.length) {
                setWarningText(data.insertContentItemsFromHtml.warnings[0])
            } else if (data?.insertContentItemsFromHtml?.instanceId) {
                setInstanceId(data?.insertContentItemsFromHtml?.instanceId)
            } else {
                // No warnings or instance ID returned by mutation, so we can't proceed;
                handlePasteError(
                    'Server returned an invalid response; cancelling paste.'
                )
            }
        },
        onError: (error) => handlePasteError(error.message),
    })

    const confirmationDialogContent = React.useMemo(() => <>{warningText}</>, [
        warningText,
    ])

    const handleCancel = React.useCallback(() => {
        onPasteCancelled?.()
    }, [onPasteCancelled])

    const handleProceed = React.useCallback(
        (_) => {
            setWarningText('')
            setIsWarningIgnored(true)
            setIsPasteInProgress(false)
        },
        [warningText]
    )

    const handlePasteError = React.useCallback(
        (errorMessage: string | null | undefined) => {
            onPasteError?.(errorMessage)
        },
        [onPasteError]
    )

    React.useEffect(() => {
        if (contentInsertVariables && !instanceId && !isPasteInProgress) {
            // Call mutation to fire off content-insert process and get an instance ID we can poll on.
            setIsPasteInProgress(true)
            pasteContentItemsFromHtml({
                variables: {
                    input: {
                        ...contentInsertVariables.input!,
                        ignoreValidationWarnings: isWarningIgnored,
                    },
                    partType: contentInsertVariables.partType!,
                    productFamilyId: contentInsertVariables.productFamilyId!,
                    rootArticleContentItemIds: contentInsertVariables.rootArticleContentItemIds!,
                },
            })
            return
        }

        setStatusText(instanceId ? StatusText_Importing : StatusText_Validating)

        if (instanceId && isPasteInProgress) {
            queryForStatus()
        }
    }, [
        contentInsertVariables,
        queryForStatus,
        statusText,
        isPasteInProgress,
        instanceId,
        isWarningIgnored,
    ])

    return warningText ? (
        <ConfirmationDialog
            dialogHeading="Warning!"
            confirmMessage="Are you sure you want to proceed with this paste?"
            confirmText="Yes"
            openDialog={!!warningText}
            handleDialogClose={handleCancel}
            dialogActionCallback={handleProceed}
            contentNode={confirmationDialogContent}
            size="small"
        />
    ) : (
        <ProgressDialog
            dialogId="html-para-paste-dialog"
            dialogName="Paste in Progress"
            open={true}
            progressPercentage={progress > 100 ? 100 : progress}
            statusText={statusText}
        />
    )
}

export default HtmlParagraphPasteProgressDialog
